import { ArticleLayout } from '#app/features/writing/article-layout.tsx'
import { SacksTweet } from '#app/features/writing/components/sacks-tweet.tsx'

export const article = {
	title: 'How to find your passion (and a good SaaS idea)',
	date: '2026-08-05',
	description:
		'Two heuristics that answer two supposedly hard questions the same way: stop introspecting and go read a record. Your card statement already knows what you love; the spreadsheets around you already know what software is missing.',
	slug: 'how-to-find-your-passion',
	draft: false,
	hardProblem: "What is your passion? Lots of people don't know.",
	approach:
		'A card statement is a ledger of what you choose when nobody is watching. And everyone has a large spreadsheet in their lives that could be a product.',
	tags: ['SaaS', 'Ideas'],
}

export default (props) => <ArticleLayout article={article} {...props} />

The way out of both questions is the same move: **stop asking yourself, and go
read a record.**

## The statement heuristic

Pull up your card statement and sort the year by merchant. Skip rent, utilities,
groceries — the obligations. What's left is a ledger of what you choose when
nobody is watching and nothing is forcing you. The category you keep paying for
— gear, courses, entry fees, tools, books on one subject — is not what you _say_
your passion is. It's what your own behaviour has already recorded it to be.

## The spreadsheet heuristic

The SaaS version of the same move is David Sacks's:

<SacksTweet />

A hand-maintained spreadsheet is the business equivalent of the card statement:
a record of need, kept up not because anyone enjoys it but because someone needs
the answer badly enough to fake the software by hand, cell by cell. You don't
have to guess whether the product is wanted — the spreadsheet's existence _is_
the evidence, the way the statement's totals are.

Two different questions, two different records. The statement is about a user; the
spreadsheet is about a product.

When I ran the second one, I went hunting for a spreadsheet worth replacing and
started noticing how people abuse them: Goal Seek dragged back and forth until
the model says the number you need, and the trail of saved copies — _v1, v2,
final, final-FINAL_ — because a sheet forgets the instant you overwrite a cell.
That second abuse pulled a thread that ended somewhere I didn't expect: how a
book should keep an editable present alongside a
[versioned past](/writing/immutable-ledger), and what accounting software looks
like when the ledger is
[derived rather than typed in](/writing/declarative-accounting).
